import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
// import { RootStoreContext } from '../stores/root_store';

import { CollabInvite, CollabInviteProps } from '../stores/collab_invite_store';
// import { Collab } from '../stores/collab_store';
import useStores from '../hooks/useStores';
import useUserIdentity from '../hooks/useUserIdentity';
import useWorkKey from 'hooks/useWorkKey';


export type CollabInviteContextType = {
    create: (collabKey: string) => Promise<CollabInvite>;
    acceptCollabInvite: (collabInviteKey: string, identityKey: string) => Promise<CollabInvite>;
    inviteMutual: (workKey: string, collabKey: string, mutualIdentityKey: string) => Promise<CollabInvite>;
    inviteDedicated: (collabKey: string, identityKey: string) => Promise<CollabInvite>;
    inviteDedicatedSpecific: (workKey: string, collab: string, assistant_identity: string) => Promise<CollabInvite>;
    inviteSupport: (collabKey: string, identityKey: string) => Promise<CollabInvite>;
    inviteTeamMember: (workKey: string, associated_type: string, associated_uuid: string, identityKey: string, message: string) => Promise<CollabInvite>;
    dedicatedInviteDayOne: (collabKey: string, identityKey: string) => Promise<CollabInvite>;
    getInvitesForCollab: (collabKey: string) => CollabInvite[];
    fetchAvailableCollabs: (workKey: string) => Promise<CollabInvite[]>;
    fetchInvitesForCollab: (collabKey: string) => Promise<CollabInvite[]>;
    fetchCollabInvite: (workKey: string, collabInviteKey: string) => Promise<CollabInviteProps|null>;
    supportJoinCollab: (workKey: string, collabKey: string) => Promise<CollabInvite>;
    getCollabInvite: (collabInviteKey: string) => CollabInviteProps | undefined;
    getAvailableInvites: () => CollabInvite[];
    getMutualInvites: (identityKey: string) => CollabInvite[];
}


export const CollabInviteContext = React.createContext<CollabInviteContextType>({
    create: () => { throw "Not implemented" },
    acceptCollabInvite: () => { throw "Not implemented" },
    inviteMutual: () => { throw "Not implemented" },
    inviteDedicated: () => { throw "Not implemented" },
    inviteDedicatedSpecific: () => { throw "Not implemented" },
    inviteSupport: () => { throw "Not implemented" },
    inviteTeamMember: () => { throw "Not implemented" },
    dedicatedInviteDayOne: () => { throw "Not implemented" },
    getInvitesForCollab: () => { throw "Not implemented" },
    fetchAvailableCollabs: () => { throw "Not implemented" },
    fetchInvitesForCollab: () => { throw "Not implemented" },
    fetchCollabInvite: () => { throw "Not implemented" },
    supportJoinCollab: () => { throw "Not implemented" },
    getCollabInvite: () => { throw "Not implemented" },
    getAvailableInvites: () => { throw "Not implemented" },
    getMutualInvites: () => { throw "Not implemented" }
});


export const CollabInviteProvider = observer(({ children }: { children: React.ReactNode }) => {
    const { collabInviteStore } = useStores();
    const { currentIdentity } = useUserIdentity();
    const workKey = useWorkKey();

    useEffect(() => {
        if (!currentIdentity?.uuid) {
            return
        }

        // Load mutuals for everybody
        collabInviteStore.fetchMutualList(currentIdentity.uuid);

        // Available for assistants
        if (workKey && currentIdentity.role == 'assistant') {
            fetchAvailableCollabs(workKey);
        }
    }, [currentIdentity?.uuid, workKey]);

    async function fetchAvailableCollabs(workKey: string) {
        console.log("fetchAvailableCollabs()");
        return collabInviteStore.fetchAvailableList(workKey);
    }

    function create(collabKey: string) {
        return collabInviteStore.create(collabKey, currentIdentity.uuid);
    }

    function inviteMutual(workKey: string, collabKey: string, mutualIdentityKey: string) {
        return collabInviteStore.inviteMutual(workKey, collabKey, mutualIdentityKey);
    }

   function inviteDedicated(collabKey: string, identityKey: string) {
        return collabInviteStore.inviteDedicated(collabKey, identityKey);
   }

    function inviteDedicatedSpecific(workKey: string, collab: string, assistant_identity: string) {
          return collabInviteStore.inviteDedicatedSpecific(workKey, collab, assistant_identity);
    }

   function inviteSupport(collabKey: string, identityKey: string) {
        return collabInviteStore.inviteSupport(collabKey, identityKey);
   }

    function inviteTeamMember(workKey: string, associated_type: string, associated_uuid: string, identityKey: string, message: string) {
        return collabInviteStore.inviteTeamMember(workKey, associated_type, associated_uuid, identityKey, message);
    }

   function dedicatedInviteDayOne(collabKey: string, identityKey: string) {
        return collabInviteStore.dedicatedInviteDayOne(collabKey, identityKey);
   }

    function getInvitesForCollab(collabKey: string) {
        return collabInviteStore.getInvitesForCollab(collabKey);
    }

    async function fetchInvitesForCollab(collabKey: string) {
        return collabInviteStore.fetchList(collabKey).then(() => {
            return collabInviteStore.getInvitesForCollab(collabKey);
        });
    }


    function supportJoinCollab(workKey: string, collabKey: string) {
        return collabInviteStore.supportJoinCollab(workKey, collabKey);
    }

    function getMutualInvites(identityKey: string) {
        return collabInviteStore.getMutualInvites(identityKey).filter((i) => i.status == 'invited');
    }

    function getAvailableInvites() {
        return collabInviteStore.getInvitesByStatus('available', 'collab');
    }

    function getCollabInvite(collabInviteKey: string) {
        return collabInviteStore.items.find((i) => i.key == collabInviteKey);
    }

    function acceptCollabInvite(collabInviteKey: string, identityKey: string) {
        return collabInviteStore.doAcceptCollabInvite(collabInviteKey, identityKey);
    }

    async function fetchCollabInvite(workKey: string, collabInviteKey: string) {
        return collabInviteStore.fetchCollabInvite(workKey, collabInviteKey);
    }

    const data = {
        create: create,
        inviteSupport: inviteSupport,
        inviteMutual: inviteMutual,
        inviteDedicated: inviteDedicated,
        inviteDedicatedSpecific: inviteDedicatedSpecific,
        inviteTeamMember: inviteTeamMember,
        fetchAvailableCollabs: fetchAvailableCollabs,
        dedicatedInviteDayOne: dedicatedInviteDayOne,
        acceptCollabInvite: acceptCollabInvite,
        getInvitesForCollab: getInvitesForCollab,
        fetchInvitesForCollab: fetchInvitesForCollab,
        fetchCollabInvite: fetchCollabInvite,
        supportJoinCollab: supportJoinCollab,
        getMutualInvites: getMutualInvites,
        getAvailableInvites: getAvailableInvites,
        getCollabInvite: getCollabInvite,
    };

    return (
        <CollabInviteContext.Provider
            value={data}>{ children }</CollabInviteContext.Provider>
    );

});
